import React from 'react';
import {
  Box,
  Stack,
  Chip,
  Typography,
  Button,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import ViewStreamOutlinedIcon from '@mui/icons-material/ViewStreamOutlined';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';

export default function SpeakerToolbar({
  speakers,
  getSpeakerColor,
  activeSpeaker,
  onSpeakerClick,
  onOpenAssistant,
  onOpenAiAssistant,
  onDownload,
  viewMode,
  onViewModeChange,
}) {
  const hasActions = Boolean(
    onOpenAssistant || onOpenAiAssistant || onDownload || onViewModeChange,
  );

  return (
    <Stack spacing={1.25} sx={{ mb: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ minWidth: 0 }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ flexShrink: 0 }}
        >
          Sprecher:
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            overflowX: 'auto',
            overflowY: 'hidden',
            minWidth: 0,
            py: 0.5,
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: 2,
            },
          }}
        >
          {speakers.map((speaker) => (
            <Chip
              key={speaker}
              label={speaker}
              size="small"
              variant={activeSpeaker === speaker ? 'filled' : 'outlined'}
              color={activeSpeaker === speaker ? 'primary' : 'default'}
              onClick={() => onSpeakerClick?.(speaker)}
              icon={
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: getSpeakerColor(speaker),
                    ml: '8px !important',
                  }}
                />
              }
              sx={{ flexShrink: 0, height: 24, fontSize: '0.75rem' }}
            />
          ))}
          {activeSpeaker && (
            <Chip
              label="Alle"
              size="small"
              variant="outlined"
              onClick={() => onSpeakerClick?.(null)}
              sx={{ flexShrink: 0, height: 24, fontSize: '0.75rem' }}
            />
          )}
        </Box>
      </Stack>

      {hasActions && (
        <>
          <Divider flexItem sx={{ opacity: 0.5 }} />
          <Stack
            direction="row"
            spacing={1}
            justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
            sx={{ flexWrap: 'wrap', rowGap: 1, alignItems: 'center' }}
          >
            {onViewModeChange && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={viewMode || 'standard'}
                onChange={(_, v) => v && onViewModeChange(v)}
                aria-label="Ansicht"
                sx={{ mr: 'auto' }}
              >
                <ToggleButton value="standard" aria-label="Standard-Ansicht">
                  <Tooltip title="Standard (ein Segment je Karte)">
                    <ViewStreamOutlinedIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="compact" aria-label="Kompakte Ansicht">
                  <Tooltip title="Kompakt (zusammenhängender Sprecher-Block)">
                    <ViewAgendaOutlinedIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            {onOpenAssistant && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<RecordVoiceOverIcon />}
                onClick={onOpenAssistant}
                sx={{ minWidth: 156 }}
              >
                Sprecher benennen
              </Button>
            )}
            {onOpenAiAssistant && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={onOpenAiAssistant}
                sx={{ minWidth: 184 }}
              >
                Zum KI-Chat springen
              </Button>
            )}
            {onDownload && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={onDownload}
                sx={{ minWidth: 136 }}
              >
                Herunterladen
              </Button>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
}
